import React, { type MouseEvent } from 'react';

import * as styles from './styles';
import { ActionButton } from './ActionButton';
import { TableCell } from './TableCell/TableCell';
import { TableHead } from './TableHead/TableHead';
import { TablePagination } from './TablePagination/TablePagination';
import { TableRow } from './TableRow/TableRow';
import { TableSorter } from './TableSorter';
import type { ITableColumn, ITableRow, SelectionConfig } from './Types';
import { Table as BaseTable, TableBody, Typography } from '@material-ui/core';

import type { SortOrder } from '@cobliteam/common-types';

import { HintPopup } from '../HintPopup/HintPopup';
import { Checkbox } from '../SelectionControls';

interface IState<T extends ITableRow> {
  order: SortOrder;
  orderBy: ITableColumn<T>;
  rowsPerPage: number;
  currentPage: number;
}

export enum ActionButtonType {
  DEFAULT = 'DEFAULT',
  DELETE = 'DELETE',
}

interface IProps<T extends ITableRow> {
  orderBy: ITableColumn<T>;
  rows: T[];
  selected: T[];
  unselectables?: { row: T; reason?: string }[];
  columns: Array<ITableColumn<T>>;
  tableTitle: string;
  className?: string;
  id?: string;
  totalRowsLength?: number;
  rowsPerPage?: number;
  currentPage?: number;
  pagination?: boolean;
  backendPagination?: boolean;
  customActionButtonName?: string;
  ActionIcon?: any;
  actionButtonType?: ActionButtonType;
  selectionConfig?: SelectionConfig;
  order?: SortOrder;
  baseTableCustomClassName?: string;
  onRowSelection?: (row: T, selected: boolean) => void;
  onSelectAllClick?: (checked: boolean) => void;
  onEdit?: (row: T) => void;
  onClick?: (row: T, column: ITableColumn<T>) => void;
  onPaginationChange?: (newPage: number, newRowsPerPage: number) => void;
  onRequestSort?: (column: ITableColumn<T>, order: SortOrder) => void;
  onMouseEnterRow?: (row: T) => (event: React.MouseEvent<HTMLTableRowElement>) => void;
  onMouseLeaveRow?: (row: T) => (event: React.MouseEvent<HTMLTableRowElement>) => void;
  onCallbackPaginationButton?: () => void;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class Table<T extends ITableRow> extends React.PureComponent<IProps<T>, IState<T>> {
  constructor(props: IProps<T>) {
    super(props);
    this.state = {
      order: props.order || 'asc',
      orderBy: props.orderBy,
      rowsPerPage: 25,
      currentPage: 0,
    };
  }

  private handleRequestSort = (column: ITableColumn<T>) => {
    const { backendPagination, orderBy: propsOrderBy, order: propsOrder } = this.props;
    const { orderBy: stateOrderBy, order: stateOrder } = this.state;
    const order =
      JSON.stringify((backendPagination && propsOrderBy) || stateOrderBy) === JSON.stringify(column) && ((backendPagination && propsOrder) || stateOrder) === 'desc'
        ? 'asc'
        : 'desc';

    if (this.props.onRequestSort) {
      this.props.onRequestSort(column, order);
    }

    this.setState({ order, orderBy: column });
  };

  private handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => this.props.onSelectAllClick?.(event.target.checked);

  private handleEditClick = (row: T) => () => {
    if (this.props.onEdit !== undefined) {
      this.props.onEdit(row);
    }
  };

  private handleClick = (row: T, column: ITableColumn<T>) => () => {
    if (this.props.onClick !== undefined) {
      this.props.onClick(row, column);
    }
  };

  private handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    const { onPaginationChange } = this.props;
    this.setState({ currentPage: newPage });

    if (onPaginationChange) {
      onPaginationChange(newPage, this.props.rowsPerPage || this.state.rowsPerPage);
    }
  };

  private handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { onPaginationChange } = this.props;
    const newRowsPerPage = Number.parseInt(event.target.value, 10);
    this.setState({ currentPage: 0, rowsPerPage: newRowsPerPage });

    if (onPaginationChange) {
      onPaginationChange(0, newRowsPerPage);
    }
  };

  private handleSelect = (row: T) => (event: React.ChangeEvent<HTMLInputElement>) => this.props.onRowSelection?.(row, event.target.checked);

  private selectedIndex = (row: T) => this.props.selected.findIndex((selected) => selected.id === row.id);

  private isSelected = (row: T) => this.selectedIndex(row) !== -1;

  private unselectableIndex = (row: T) =>
    this.props.unselectables ? this.props.unselectables.map((unselectable) => unselectable.row).findIndex((selected) => selected.id === row.id) : -1;

  private isUnselectable = (row: T) => this.unselectableIndex(row) !== -1;

  private getUnselectableReason = (row: T) => this.props.unselectables?.find((unselectable) => unselectable.row.id === row.id)?.reason;

  public render() {
    const {
      tableTitle,
      onEdit,
      selected,
      unselectables,
      rows,
      columns,
      selectionConfig,
      pagination,
      backendPagination,
      className,
      id,
      customActionButtonName,
      ActionIcon,
      totalRowsLength,
      actionButtonType,
      baseTableCustomClassName,
      onMouseEnterRow,
      onMouseLeaveRow,
      currentPage: propsCurrentPage,
      rowsPerPage: propsRowsPerPage,
      orderBy: propsOrderBy,
      order: propsOrder,
      onCallbackPaginationButton,
    } = this.props;

    const { currentPage: stateCurrentPage, rowsPerPage: stateRowsPerPage, orderBy: stateOrderBy, order: stateOrder } = this.state;

    const currentPage = (backendPagination && propsCurrentPage) || stateCurrentPage;
    const rowsPerPage = (backendPagination && propsRowsPerPage) || stateRowsPerPage;
    const orderBy = (backendPagination && propsOrderBy) || stateOrderBy;
    const order = (backendPagination && propsOrder) || stateOrder;

    const wantedRows = backendPagination ? rows : TableSorter.sort(rows, order, orderBy).slice();
    const renderedRows = pagination && !backendPagination ? wantedRows.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage) : wantedRows;

    const rowCount = rows.length - (unselectables?.map((unselectable) => unselectable.row).length || 0);

    return (
      <div className={className}>
        <BaseTable id={id} aria-labelledby={tableTitle} className={baseTableCustomClassName}>
          <TableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={this.handleSelectAllClick}
            onRequestSort={this.handleRequestSort}
            rowCount={rowCount}
            columns={columns}
            selectionConfig={selectionConfig}
            editable={onEdit !== undefined}
          />
          <TableBody>
            {renderedRows.map((row) => {
              const isSelected = this.isSelected(row);
              const isUnselectable = this.isUnselectable(row);
              const unselectableReason = this.getUnselectableReason(row);

              return (
                <TableRow
                  role={selectionConfig?.selectable ? 'checkbox' : 'row'}
                  aria-checked={isSelected}
                  tabIndex={-1}
                  key={row.id}
                  id={row.id}
                  selected={isSelected}
                  onMouseEnter={onMouseEnterRow ? onMouseEnterRow(row) : undefined}
                  onMouseLeave={onMouseLeaveRow ? onMouseLeaveRow(row) : undefined}
                >
                  {selectionConfig?.selectable && (
                    <TableCell className={selectionConfig?.className}>
                      <HintPopup title={unselectableReason || ''} placement='bottom-start' enabled={Boolean(isUnselectable && unselectableReason)} arrow noArrowDisplacement>
                        <span className={styles.checkboxSpan}>
                          <Checkbox checked={isSelected} onChange={this.handleSelect(row)} disabled={this.isUnselectable(row)} />
                        </span>
                      </HintPopup>
                    </TableCell>
                  )}
                  {columns.map((column: ITableColumn<T>) => {
                    if (column.hide) {
                      return null;
                    }
                    const Cell = column.Cell(row);
                    return (
                      <TableCell
                        className={column.classNameFunc ? column.classNameFunc(row) : column.className}
                        key={`${row.id}.${column.id}`}
                        onClick={this.handleClick(row, column)}
                      >
                        {React.isValidElement(Cell) ? (
                          Cell
                        ) : (
                          <Typography variant='inherit' noWrap>
                            {Cell}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  {onEdit && (
                    <TableCell>
                      <ActionButton
                        hideButton={row.shouldHideActionButton}
                        onClick={this.handleEditClick(row)}
                        actionButtonName={customActionButtonName}
                        ActionIcon={ActionIcon}
                        actionButtonType={actionButtonType}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </BaseTable>
        {!!wantedRows.length && pagination && (
          <TablePagination
            count={totalRowsLength || wantedRows.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onCallbackPaginationButton={onCallbackPaginationButton}
            onPageChange={this.handleChangePage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
          />
        )}
      </div>
    );
  }
}
